import type { BoView, EdiAnchor, MappingEntry, Skeleton, Transform } from "@/api/mapperApi";

/** EDIFACT service/envelope segments — never shown in the Brücke. */
export const SERVICE_SEGMENTS = new Set([
  "UNA", "UNB", "UNG", "UNH", "UNT", "UNE", "UNZ", "UNS", "CNT",
]);

export type Perspective = "msgtype" | "bo";
export type Side = "edi" | "bo4e";

/** A field shown on either side of a row. */
export interface DisplayField {
  segment?: string; // EDI segment, e.g. "BGM"
  detail?: string; // e.g. "DE1004 · C106" or "Qual 137"
  name: string; // human label
  messageType?: string; // origin tag (BO perspective, cross message type)
}

/** One opposite-side match hanging off the spine. */
export interface BridgeMatch {
  target: DisplayField;
  transform: Transform;
  mappingId: string;
  status?: string;
}

/** One spine row with its stacked matches. */
export interface BridgeRow {
  key: string;
  group: string; // grouping header: segment (EDI) or BO name (BO)
  spine: DisplayField;
  matches: BridgeMatch[]; // empty => unmapped
  mappable: boolean; // false => render greyed "kein BO4E"
  ediAnchor?: EdiAnchor; // preset for "+ mappen" (EDI perspective)
  segmentGroup?: string; // EDI perspective: SG… (or "(ohne Gruppe)")
  segName?: string; // EDI perspective: human segment name
  segNr?: string; // EDI perspective: MIG nr identifying the segment instance
}

export interface EdiBridgeOpts {
  /** Anchor nrs known to have no BO4E target (data-derived; populated by workstream #2). */
  noBo4e?: Set<string>;
}

function anchorDetail(a: EdiAnchor): string {
  const parts: string[] = [];
  if (a.data_element) parts.push(`DE${a.data_element}`);
  if (a.component) parts.push(a.component);
  if (a.qualifier) parts.push(`Qual ${a.qualifier}`);
  return parts.join(" · ");
}

/** Mirrors the backend match logic: nr when present, else segment+qualifier+data_element. */
function entryMatchesAnchor(e: MappingEntry, a: EdiAnchor): boolean {
  const ea = e.edi_anchor;
  if (a.nr && ea.nr) return ea.nr === a.nr;
  return (
    (ea.segment || "").toUpperCase() === (a.segment || "").toUpperCase() &&
    (ea.qualifier || "") === (a.qualifier || "") &&
    (ea.data_element || "") === (a.data_element || "")
  );
}

/** Build Brücke rows for the business-object perspective (BO field is the spine). */
export function buildBoBridge(view: BoView): BridgeRow[] {
  return view.fields.map((f) => ({
    key: `bo:${view.bo}:${f.path}`,
    group: view.bo,
    spine: { name: f.path },
    matches: f.sources.map((s) => ({
      target: {
        segment: s.edi_anchor.segment,
        detail: anchorDetail(s.edi_anchor),
        name: s.edi_anchor.de_name || s.edi_anchor.seg_name || s.edi_anchor.segment,
        messageType: s.message_type,
      },
      transform: s.transform,
      mappingId: s.mapping_id,
      status: s.status,
    })),
    mappable: true,
  }));
}

/** Which logical side renders in the left/right column. Default: EDI left, BO4E right.
 * `swapped` flips them. The spine side depends on perspective (EDI for msgtype, BO4E
 * for bo) but the component decides spine vs match placement from this + perspective. */
export function sidesFor(
  _perspective: Perspective,
  swapped: boolean,
): { left: Side; right: Side } {
  return swapped ? { left: "bo4e", right: "edi" } : { left: "edi", right: "bo4e" };
}

/** Build Brücke rows for the message-type perspective (EDI is the spine). */
export function buildEdiBridge(
  skeleton: Skeleton,
  entries: MappingEntry[],
  opts: EdiBridgeOpts,
): BridgeRow[] {
  const noBo4e = opts.noBo4e ?? new Set<string>();
  const rows: BridgeRow[] = [];
  skeleton.edi_anchors.forEach((a, i) => {
    if (SERVICE_SEGMENTS.has((a.segment || "").toUpperCase())) return;
    const matches: BridgeMatch[] = entries
      .filter((e) => entryMatchesAnchor(e, a))
      .map((e) => ({
        target: { name: `${e.bo4e_anchor.bo}.${e.bo4e_anchor.path}` },
        transform: e.transform,
        mappingId: e.mapping_id,
        status: e.status,
      }));
    rows.push({
      // Prefix with the anchor's skeleton index: real MIG skeletons repeat the
      // same nr across segment instances, so nr alone is not unique. The index
      // is stable for a given skeleton, keeping React keys stable across renders.
      key: `edi:${i}:${a.nr ?? `${a.segment}:${a.data_element}:${a.qualifier ?? ""}`}`,
      group: a.segment,
      spine: { segment: a.segment, detail: anchorDetail(a), name: a.de_name || a.seg_name || a.segment },
      matches,
      mappable: !(a.nr && noBo4e.has(a.nr)),
      ediAnchor: a,
      segmentGroup: a.segment_group ?? "(ohne Gruppe)",
      segName: a.seg_name ?? undefined,
      segNr: a.nr,
    });
  });
  return rows;
}

export interface SegmentBlock { key: string; segment: string; segName?: string; rows: BridgeRow[]; mappedCount: number; }
export interface SegGroupBlock { segmentGroup: string; segments: SegmentBlock[]; mappedCount: number; }

/** Group EDI-perspective rows into Segmentgruppe › Segment blocks, preserving order.
 * A new SegmentBlock starts when the segment INSTANCE changes (identified by segNr,
 * falling back to the segment code when segNr is absent), so multiple instances of the
 * same segment code in one group (e.g. several NAD segments) become separate blocks. */
export function groupEdiRows(rows: BridgeRow[]): SegGroupBlock[] {
  const groups: SegGroupBlock[] = [];
  for (const r of rows) {
    const sg = r.segmentGroup ?? "(ohne Gruppe)";
    const seg = r.spine.segment ?? r.group;
    const instance = r.segNr ?? seg;
    let g = groups[groups.length - 1];
    if (!g || g.segmentGroup !== sg) { g = { segmentGroup: sg, segments: [], mappedCount: 0 }; groups.push(g); }
    let s = g.segments[g.segments.length - 1];
    const sInstance = s ? (s.rows[0]?.segNr ?? s.segment) : undefined;
    if (!s || sInstance !== instance) {
      s = { key: `${sg}|${instance}`, segment: seg, segName: r.segName, rows: [], mappedCount: 0 };
      g.segments.push(s);
    }
    s.rows.push(r);
    const m = r.matches.length > 0 ? 1 : 0;
    s.mappedCount += m; g.mappedCount += m;
  }
  return groups;
}
